import dayjs from "dayjs";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkline, monthLabel, type MonthKey } from "@/components/team/MonthPicker";
import type { MonthlyStat } from "@/types/domain";

/** Cuántos meses de contexto se dibujan al lado del número. */
const TREND_MONTHS = 3;

/**
 * Una métrica del mes: cuánto cambió contra el mes anterior, la tendencia y los
 * valores crudos. El delta es la lectura rápida; la tendencia evita confundir un
 * buen mes con una racha.
 */
function MetricRow({
  label,
  series,
  months,
}: {
  label: string;
  series: number[];
  months: MonthKey[];
}) {
  const current = series[series.length - 1] ?? 0;
  const previous = series[series.length - 2] ?? 0;
  const delta = current - previous;

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span
          className={cn(
            "text-xl font-extrabold tabular-nums",
            delta > 0 && "text-emerald-600 dark:text-emerald-400",
            delta < 0 && "text-red-500",
            delta === 0 && "text-slate-400",
          )}
        >
          {delta > 0 ? `+${delta}` : delta}
        </span>
        <span className="truncate text-[13px] font-semibold text-slate-600 dark:text-slate-300">
          {label}
        </span>
      </span>

      <Sparkline values={series} months={months} label={label} />
    </div>
  );
}

/**
 * El ritmo del mes contra el anterior: cuánto entró al pipeline y cuánto se cerró.
 * Es la única tarjeta que mira hacia atrás — las demás dicen cómo está el pipeline
 * hoy, esta dice si va mejor o peor que antes.
 */
export function MonthComparison({
  stats,
  month,
  isLoading,
  scopeLabel,
}: {
  stats: MonthlyStat[];
  month: MonthKey;
  isLoading?: boolean;
  scopeLabel: string;
}) {
  // La ventana termina en el mes elegido: comparar septiembre mirando la serie
  // completa daría el delta del mes en curso, no el del mes que se está viendo.
  const upToMonth = stats.filter((stat) => stat.month <= month);
  const window = upToMonth.slice(-TREND_MONTHS);
  const months = window.map((stat) => stat.month);
  const previousLabel =
    months.length > 1 ? monthLabel(months[months.length - 2]!) : null;

  return (
    <div className="flex flex-col gap-2 rounded-2xl border bg-card p-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {previousLabel ? `vs. ${previousLabel}` : "Ritmo"} · {scopeLabel}
      </p>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      ) : window.length === 0 ? (
        <p className="py-2 text-sm text-slate-400">
          Todavía no hay historial para comparar.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <MetricRow
              label="guardadas"
              series={window.map((stat) => stat.saved)}
              months={months}
            />
            {/* Ganadas y no cerradas: "cerradas" suma las perdidas, y ahí el
                verde del delta diría que perder más es una buena noticia. */}
            <MetricRow
              label="ganadas"
              series={window.map((stat) => stat.won)}
              months={months}
            />
          </div>
          <p className="font-mono text-[10px] tabular-nums text-slate-400">
            {months
              .map((m) => dayjs(`${m}-01`).format("MMM"))
              .join(" · ")}
          </p>
        </>
      )}
    </div>
  );
}
